// useUrlSync — keeps the URL hash in sync with the active scene and
// the planet mode, and mirrors that into the document.title.
//
// Mounted once at the app root. Three responsibilities:
//
//   1. On every scene-index / mode change, write a debounced (300ms)
//      `history.replaceState` that updates the hash. We use replaceState
//      instead of `location.hash = …` because the latter causes the
//      browser to scroll to the top of the page, which is the opposite
//      of what we want during a continuous scroll.
//
//   2. Update document.title to "yhazrin — {Scene Name}". The
//      original <title> from index.html is preserved as the baseline
//      and restored on `beforeunload` so a closed tab doesn't leave a
//      stale title in browser history.
//
//   3. On initial load, if the URL points at a specific scene, scroll
//      to the middle of that scene so the user lands in the right
//      place. If the hash is `#explore`, switch into exploration mode.
//
// Back / forward navigation is handled by `popstate` — when the user
// pops to a previous hash we scroll to that scene. We deliberately
// do NOT push entries onto the history stack (replaceState only) so
// a long scroll session does not fill the back button with per-frame
// entries.

import { useEffect } from 'react';

// Mirror the title <title>yhazrin — software engineer, personal planet observatory</title>
// in index.html. Kept in sync manually — if you change one, change both.
const ORIGINAL_TITLE =
  'yhazrin — software engineer, personal planet observatory';

/** Per-scene title suffix. Index-aligned with SCENES. */
const SCENE_TITLES: readonly string[] = [
  'Wake', // 0 — intro
  'About',
  'Skills',
  'Projects',
  'Notes',
  'Timeline',
  'Contact',
];

/** Per-scene URL key. Index-aligned with SCENES. */
const SCENE_KEYS: readonly string[] = [
  'intro',
  'about',
  'skills',
  'projects',
  'notes',
  'timeline',
  'contact',
];

/** Scene start/end in normalized 0..1 scroll progress — mirrors SCENES. */
const SCENE_BOUNDS: readonly { start: number; end: number }[] = [
  { start: 0, end: 0.15 },
  { start: 0.15, end: 0.3 },
  { start: 0.3, end: 0.45 },
  { start: 0.45, end: 0.65 },
  { start: 0.65, end: 0.8 },
  { start: 0.8, end: 0.92 },
  { start: 0.92, end: 1.0 },
];

/** Number of milliseconds to wait after the last scene/mode change
 *  before writing the hash. Long enough that a quick scroll past a
 *  scene boundary doesn't churn replaceState, short enough that the
 *  URL is up to date when the user copies it from the address bar. */
const DEBOUNCE_MS = 300;

/** Build the hash string for the current state. */
function buildHash(idx: number, isExplore: boolean): string {
  if (isExplore) return '#explore';
  const key = SCENE_KEYS[idx] ?? 'intro';
  return '#scene=' + key;
}

/** Build the document.title for the current state. */
function buildTitle(idx: number, isExplore: boolean): string {
  const suffix = isExplore
    ? 'Explore'
    : (SCENE_TITLES[idx] ?? SCENE_TITLES[0]);
  return `yhazrin — ${suffix}`;
}

/** Read the current scene/mode snapshot. */
function readSnapshot(): { idx: number; isExplore: boolean } {
  const idx =
    typeof window !== 'undefined' && typeof window.__planetSceneIndex === 'number'
      ? window.__planetSceneIndex
      : 0;
  const isExplore =
    typeof window !== 'undefined' && window.__planetMode === 'exploration';
  return { idx, isExplore };
}

/** Read just the mode. */
function readMode(): 'narrative' | 'exploration' {
  return typeof window !== 'undefined' && window.__planetMode === 'exploration'
    ? 'exploration'
    : 'narrative';
}

/** Write the current hash to the URL via replaceState. No-op if the
 *  hash already matches. Skips when no hash is desired (intro +
 *  narrative) so the URL stays clean. */
function writeHash(hash: string): void {
  if (typeof window === 'undefined') return;
  const target = window.location.pathname + window.location.search + hash;
  const current = window.location.pathname + window.location.search + window.location.hash;
  if (current === target) return;
  try {
    window.history.replaceState({}, '', target);
  } catch {
    /* some embedders block history writes — fail silently */
  }
}

/** Scroll to the middle progress of the given scene index. */
function scrollToScene(i: number, behavior: ScrollBehavior = 'smooth'): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const b = SCENE_BOUNDS[i];
  if (!b) return;
  const max = document.documentElement.scrollHeight - window.innerHeight;
  if (max <= 0) return;
  const target = ((b.start + b.end) / 2) * max;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({
    top: target,
    behavior: reduced ? 'auto' : behavior,
  });
}

/** Parse the current hash into a { kind, value } descriptor. */
type HashDescriptor =
  | { kind: 'none' }
  | { kind: 'scene'; key: string; idx: number | null }
  | { kind: 'explore' }
  | { kind: 'unknown'; key: string };

function parseHash(rawHash: string): HashDescriptor {
  const h = rawHash.replace(/^#/, '');
  if (!h) return { kind: 'none' };
  if (h === 'explore') return { kind: 'explore' };
  const m = h.match(/^scene=([^&]+)/);
  if (m) {
    const key = m[1];
    const idx = SCENE_KEYS.indexOf(key);
    return { kind: 'scene', key, idx: idx >= 0 ? idx : null };
  }
  return { kind: 'unknown', key: h };
}

/** Find a scene index by key, or null if unknown. */
function indexForKey(key: string): number | null {
  const i = SCENE_KEYS.indexOf(key);
  return i >= 0 ? i : null;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useUrlSync — installs the URL hash + document.title sync.
 * Mount once at the app root.
 */
export function useUrlSync(): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // -- 0. Restore original title on unload -----------------------------
    // Closed tabs sometimes retain the last document.title in their
    // browser-history card. We don't want "yhazrin — Skills" sitting
    // there forever; the original index.html title is the right thing
    // for a passive history entry.
    const onBeforeUnload = () => {
      try {
        document.title = ORIGINAL_TITLE;
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    // -- 1. Initial-load handling ----------------------------------------
    // If the URL already carries a scene= or explore hash, scroll into
    // position and set the title accordingly. The 1-frame rAF delay
    // lets the body min-height settle so documentElement.scrollHeight
    // is correct before we measure.
    let initialHandled = false;
    const initialDesc = parseHash(window.location.hash);
    const initialMode = readMode();

    // Defer the initial scroll/title one tick so layout settles.
    const initialRaf = requestAnimationFrame(() => {
      if (initialHandled) return;
      initialHandled = true;

      if (initialDesc.kind === 'scene' && initialDesc.idx !== null && initialDesc.idx !== 0) {
        // Jump straight to the scene's middle progress. Use 'auto' so
        // the user doesn't watch a 1.5s scroll on every cold load.
        scrollToScene(initialDesc.idx, 'auto');
        document.title = buildTitle(initialDesc.idx, false);
      } else if (initialDesc.kind === 'explore') {
        document.title = buildTitle(0, true);
        // Mode is already restored from localStorage on init; nothing
        // else to do here.
      } else if (initialDesc.kind === 'none') {
        // No hash, intro scene — leave URL clean and use the
        // original title.
        document.title = ORIGINAL_TITLE;
      } else if (initialDesc.kind === 'scene' && initialDesc.idx === 0) {
        // #scene=intro — same as no hash.
        document.title = ORIGINAL_TITLE;
      } else {
        // Unknown hash — leave the URL as-is but use the original
        // title so the tab doesn't claim a fake scene.
        document.title = ORIGINAL_TITLE;
      }
      // Suppress an unused-var lint while keeping the local for clarity.
      void initialMode;
    });

    // -- 2. Poll scene + mode, debounce, write ---------------------------
    let lastIdx =
      typeof window.__planetSceneIndex === 'number' ? window.__planetSceneIndex : 0;
    let lastMode: 'narrative' | 'exploration' =
      window.__planetMode === 'exploration' ? 'exploration' : 'narrative';
    let debounceId: number | null = null;
    let lastWrittenHash = window.location.hash;
    let lastWrittenTitle = document.title;

    const flush = () => {
      debounceId = null;
      const { idx, isExplore } = readSnapshot();
      const hash = buildHash(idx, isExplore);
      const title = buildTitle(idx, isExplore);

      // For the intro+narrative combo, leave the hash empty so the URL
      // stays clean. Treat "no hash desired" as a separate target so we
      // can clear it.
      const desiredHash = isExplore || idx > 0 ? hash : '';
      if (desiredHash !== lastWrittenHash) {
        writeHash(desiredHash);
        lastWrittenHash = desiredHash;
      }
      if (title !== lastWrittenTitle) {
        document.title = title;
        lastWrittenTitle = title;
      }
    };

    const schedule = () => {
      if (debounceId !== null) window.clearTimeout(debounceId);
      debounceId = window.setTimeout(flush, DEBOUNCE_MS);
    };

    const id = window.setInterval(() => {
      const curIdx =
        typeof window.__planetSceneIndex === 'number' ? window.__planetSceneIndex : 0;
      const curMode = readMode();
      if (curIdx !== lastIdx || curMode !== lastMode) {
        lastIdx = curIdx;
        lastMode = curMode;
        schedule();
      }
    }, 100);

    // -- 3. popstate — back / forward navigation -------------------------
    // Both `popstate` and `hashchange` fire on back/forward in modern
    // browsers, but popstate is the canonical event and is the one the
    // brief asks for. We handle both here for safety, deduplicated.
    const onHistoryChange = () => {
      const desc = parseHash(window.location.hash);
      if (desc.kind === 'scene' && desc.idx !== null) {
        scrollToScene(desc.idx);
      }
      // Update the title snapshot so a subsequent replaceState doesn't
      // re-write the same title (cosmetic, prevents a flicker).
      const snap = readSnapshot();
      const title = buildTitle(
        desc.kind === 'scene' && desc.idx !== null ? desc.idx : snap.idx,
        desc.kind === 'explore' || snap.isExplore,
      );
      if (document.title !== title) {
        document.title = title;
        lastWrittenTitle = title;
      }
      lastWrittenHash = window.location.hash;
    };
    window.addEventListener('popstate', onHistoryChange);
    window.addEventListener('hashchange', onHistoryChange);

    // -- 4. Cleanup ------------------------------------------------------
    return () => {
      window.clearInterval(id);
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('popstate', onHistoryChange);
      window.removeEventListener('hashchange', onHistoryChange);
      if (debounceId !== null) window.clearTimeout(debounceId);
      cancelAnimationFrame(initialRaf);
    };
  }, []);
}

/** Build the share URL for the given scene index / mode. Exported so
 *  the HUDPanel's copy button can construct it without depending on
 *  the hook's internals. */
export function buildShareUrl(
  idx: number,
  isExplore: boolean,
): string {
  if (typeof window === 'undefined') return '';
  const base = window.location.origin + window.location.pathname + window.location.search;
  if (isExplore) return base + '#explore';
  const key = SCENE_KEYS[idx] ?? 'intro';
  if (idx === 0 && !isExplore) return base;
  return base + '#scene=' + key;
}

/** Read the current snapshot's share URL. */
export function currentShareUrl(): string {
  const { idx, isExplore } = readSnapshot();
  return buildShareUrl(idx, isExplore);
}

/** Get the current document title for the snapshot. */
export function currentShareTitle(): string {
  const { idx, isExplore } = readSnapshot();
  return buildTitle(idx, isExplore);
}

export default useUrlSync;